import type { FormFieldsValues } from '../components/Form/Common';
import type { FormSpec } from './form-specs';

export interface FormTemplate {
  id: string;
  titleKey: string;
  descriptionKey: string;
  iconName: 'shopping-cart' | 'feedback' | 'event';
  spec: FormSpec;
  /** Form-level overrides applied on top of the user's defaults. */
  formDefaults?: Partial<FormFieldsValues>;
}

const orderForm: FormSpec = {
  fields: {
    full_name: {
      name: 'full_name',
      field: 'text',
      label: 'Full Name',
      placeholder: 'Jane Doe',
      required: true,
    },
    email: {
      name: 'email',
      field: 'text',
      label: 'Email',
      placeholder: 'jane@example.com',
      is_email: true,
      required: true,
    },
    product: {
      name: 'product',
      field: 'text',
      label: 'Product or SKU',
      placeholder: 'e.g. Widget Pro / SKU-1234',
      required: true,
    },
    quantity: {
      name: 'quantity',
      field: 'number',
      label: 'Quantity',
      placeholder: '1',
      required: true,
      is_min: 1,
    },
    shipping_address: {
      name: 'shipping_address',
      field: 'textarea',
      label: 'Shipping Address',
      placeholder: '123 Main St, City, Country',
      required: true,
    },
    notes: {
      name: 'notes',
      field: 'textarea',
      label: 'Order Notes',
      placeholder: 'Any special instructions?',
      check_spam: true,
      on_fail: 'spam',
    },
  },
  settings: {
    discard_additional_fields: true,
  },
};

const userFeedback: FormSpec = {
  fields: {
    name: {
      name: 'name',
      field: 'text',
      label: 'Name',
      placeholder: 'Optional',
    },
    email: {
      name: 'email',
      field: 'text',
      label: 'Email',
      placeholder: 'you@example.com',
      is_email: true,
    },
    feedback_type: {
      name: 'feedback_type',
      field: 'select',
      label: 'Feedback Type',
      options: 'Bug Report,Feature Request,General,Praise',
      required: true,
    },
    rating: {
      name: 'rating',
      field: 'select',
      label: 'Overall Rating',
      options: '1,2,3,4,5',
      required: true,
    },
    message: {
      name: 'message',
      field: 'textarea',
      label: 'Your Feedback',
      placeholder: 'Tell us what you think…',
      required: true,
      is_min: 5,
      check_spam: true,
      on_fail: 'spam',
    },
  },
  settings: {
    discard_additional_fields: true,
  },
};

const eventRsvp: FormSpec = {
  fields: {
    full_name: {
      name: 'full_name',
      field: 'text',
      label: 'Full Name',
      placeholder: 'Jane Doe',
      required: true,
    },
    email: {
      name: 'email',
      field: 'text',
      label: 'Email',
      placeholder: 'jane@example.com',
      is_email: true,
      required: true,
    },
    attendance: {
      name: 'attendance',
      field: 'select',
      label: 'Will you attend?',
      options: 'Yes,No,Maybe',
      required: true,
    },
    guest_count: {
      name: 'guest_count',
      field: 'number',
      label: 'Number of Guests',
      placeholder: '0',
      is_min: 0,
    },
    dietary_restrictions: {
      name: 'dietary_restrictions',
      field: 'textarea',
      label: 'Dietary Restrictions',
      placeholder: 'Vegan, gluten-free, allergies…',
    },
    notes: {
      name: 'notes',
      field: 'textarea',
      label: 'Notes',
      placeholder: 'Anything else we should know?',
      check_spam: true,
      on_fail: 'spam',
    },
  },
  settings: {
    discard_additional_fields: true,
  },
};

export const FORM_TEMPLATES: FormTemplate[] = [
  {
    id: 'order-form',
    titleKey: 'account.templates.orderForm.title',
    descriptionKey: 'account.templates.startFromTemplate',
    iconName: 'shopping-cart',
    spec: orderForm,
    formDefaults: { filter_spam: true },
  },
  {
    id: 'user-feedback',
    titleKey: 'account.templates.userFeedback.title',
    descriptionKey: 'account.templates.startFromTemplate',
    iconName: 'feedback',
    spec: userFeedback,
    formDefaults: { filter_spam: true },
  },
  {
    id: 'event-rsvp',
    titleKey: 'account.templates.eventRsvp.title',
    descriptionKey: 'account.templates.startFromTemplate',
    iconName: 'event',
    spec: eventRsvp,
    formDefaults: { filter_spam: true },
  },
];
